import { useEffect, useState } from 'preact/hooks';
import {
  getPlan,
  getRecordTypeSchema,
  planHas,
  updateRecord,
  type PlanApi,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import {
  authorityGateWords,
  convertRefusalToAsk,
  AUTHORITY_GATE_LEAD,
  type AuthorityGate,
} from '../lib/authorityGate';
import { listAllRecordsByType, listAllRecordsWithLinks } from '../lib/api';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { bumpRecordsVersion, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { fmtMoney, humanizeValue } from '../lib/fieldDisplay';
import { ageInDays, ageLabel } from '../lib/aging';
import { AA, LINK, otherEnd } from '../lib/auditionSpread';
import {
  expectedOn,
  isSettled,
  NO_AMOUNT_RECORDED,
  outstandingOn,
  receivedOn,
  rowAmount,
  setAsideFor,
  splitMoney,
} from '../lib/farSide';
import { householdTab } from '../lib/householdTabs';
import './Registry.css';
// The row/list vocabulary is the spread's — this screen reuses it rather
// than growing a second set of row styles that would drift from it.
import './spread.css';
import './farSide.css';

// A5 — THE MONEY TAB. Replaces the thin Registry that stood here.
//
// ===================================================================
// TWO QUESTIONS, AND THE ORDER IS THE DESIGN
// ===================================================================
//   1. WHAT IS OWED, AND HOW LATE.      -> the chase
//   2. WAS HER SHARE SET ASIDE.         -> the obligation
//
// That is the order a parent actually has them, and it is not the order a
// ledger would choose. A ledger leads with balances; this leads with what
// requires a phone call, because a household chasing a session fee has no
// accounts-receivable clerk and the only thing that moves the money is a
// parent noticing.
//
// ===================================================================
// WHAT THIS SCREEN REFUSES TO PRETEND
// ===================================================================
// Decision cfeaa545: NO INSTITUTION BINDING. No account number, no linked
// balance, no claimed total. This product cannot see a bank account and will
// not imply that it can.
//
// So the set-aside figure is labelled ***should have been set aside***, never
// "set aside". The difference is the entire ethical position of the feature:
// one is a derivation this product can stand behind, the other is a claim
// about money movement it has no way to know. A household can be held to the
// first. Nobody should be reassured by the second.
//
// And where `set_aside_pct` is not recorded, the figure is ABSENT — not
// zero. "$0.00 should have been set aside" on a child's page is a precise,
// confident, false statement about money produced entirely by a missing
// field (decision 21e9f137's precedent, applied to a derivation).
//
// ===================================================================
// AGING IS DERIVED, AND NOT-YET-DUE IS NOT AGING
// ===================================================================
// The pack's own header says it: there is no stored age and no job keeping
// one true. `lib/aging.ts` is the product's one clock and this screen goes
// through it.
//
// But money DUE NEXT MONTH IS NOT MONEY 0-30 DAYS OLD, and `RECEIVABLE_BANDS`
// would put both in the same bucket (its first band runs from -Infinity).
// Half a household's expected money is post-dated to a shoot that has not
// happened, so that conflation would make the chase number meaningless. The
// split happens in farSide.ts's `splitMoney`; this screen renders the two
// sides differently, and the not-yet-due line is deliberately QUIET.
//
// ===================================================================
// P6 — MONEY THAT CAME IN PIECES, AND WHOSE SCREEN THIS IS
// ===================================================================
// A partially-received payment STAYS IN THE CHASE, banded by what is still
// owed rather than by what the invoice was: a household waiting on 600 of a
// 1,000 fee must not read 1,000, and must not read nothing. Every figure on
// this screen comes from the pack's own running total (`received_amount`) and
// the remainder derived from it — never from a second stored number.
//
// AND THE ACCOUNTING IS PRO'S (`accounting.contracted_income`, plan model
// §5). Two of the four sections below — the chase and the set-aside — are the
// derivation this tier sells, and they were reachable on every tier. Below
// Pro they are not drawn, the tab that leads here is not offered at all
// (the pack declares `NavTabDef.feature` and /api/team/me drops it), and a
// household arriving by an old link is TOLD, once, in a named line.
//
// ⚠ THE ROWS STAY. Payments and expenses are the household's own records and
// always were; a plan may withhold the arithmetic and never the entries.
// That is framework §5's lapse posture — soft and loud — and it is the whole
// difference between a plan and a hostage. The section labels for those two
// lists come from `householdTabs.ts`, which is where this pack's tab-to-type
// mapping lives; a second copy of "Payments / Nothing paid or owing yet"
// here is exactly the drift that table exists to prevent.

interface Props {
  tenantId: string;
}

interface State {
  loaded: boolean;
  payments: RecordRow[];
  expenses: RecordRow[];
  subjects: RecordRow[];
  paymentSchema: RecordTypeSchema | null;
  /** Which performer each money row is attributed to, resolved from its LINKS
   * — there is no `subject_id` column on a record and inventing one would
   * silently attribute every payment to nobody. */
  subjectOf: Map<string, string | null>;
  /** P6 — the tier. Null while loading AND when the read failed, and
   * `planHas(null, …)` is false: the accounting is withheld for a beat rather
   * than flashed and taken away, which is the safe direction on a screen
   * whose whole subject is a family's money. */
  plan: PlanApi | null;
}

const EMPTY: State = {
  loaded: false,
  payments: [],
  expenses: [],
  subjects: [],
  paymentSchema: null,
  subjectOf: new Map(),
  plan: null,
};

/** The two row lists' labels, from the pack's own tab table — never a second
 * copy here. Null would mean the table stopped describing `money`, which is a
 * change to make there and not a case to invent a fallback for. */
const MONEY_TAB = householdTab('money');
function sectionOf(typeKey: string): { label: string; empty: string } {
  const section = MONEY_TAB?.sections.find((s) => s.typeKey === typeKey);
  return { label: section?.label ?? typeKey, empty: section?.empty ?? '' };
}

export function HouseholdMoney({ tenantId }: Props) {
  const [state, setState] = useState<State>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [nonce, setNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  // ⚑ THE PERCENTAGE HAS A DOOR NOW (2026-09-16, the Day 3–4 walk, item 3).
  // Why it lives on THIS screen rather than in Settings is argued where it is
  // drawn — search `fs__setaside-open` in the render below.
  const [pctEdit, setPctEdit] = useState<{ subjectId: string; draft: string } | null>(null);
  const [pctBusy, setPctBusy] = useState(false);
  const [pctSay, setPctSay] = useState<{ subjectId: string; line: string; bad?: boolean } | null>(null);

  /** One field, one save, one receipt. TechDetail's pay editor is the pattern:
   * a plain text box with a decimal keypad (never `type="number"`, which fights
   * a person mid-decimal), `Number()` on the trimmed text, and an EMPTY box
   * meaning cleared rather than zero — because null is not zero is the entire
   * rule this figure lives under. */
  async function savePct(subject: RecordRow, raw: string): Promise<void> {
    const text = raw.trim();
    let value: number | null;
    if (text === '') {
      value = null;
    } else {
      const n = Number(text);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setPctSay({ subjectId: subject.id, line: 'A percentage, between 0 and 100.', bad: true });
        return;
      }
      value = n;
    }
    setPctBusy(true);
    setPctSay(null);
    try {
      await updateRecord(tenantId, subject.id, { set_aside_pct: value });
      // A receipt names what became of the thing, in this screen's own
      // vocabulary — "should have been", never "set aside".
      setPctSay({
        subjectId: subject.id,
        line:
          value === null
            ? `No percentage is recorded for ${subject.display_name}. Nothing is derived from it.`
            : `${value}% of what arrives for ${subject.display_name} should have been set aside.`,
      });
      setPctEdit(null);
      bumpRecordsVersion();
      setNonce((n) => n + 1);
    } catch (err) {
      // ⚑ THIS IS A PARENT-ONLY FIELD. `actor_assist.subject.*` is one of the
      // two acts the manifest withholds from a second adult, so her save
      // CONVERTS — and a refusal becomes a reach, once, through the one
      // module that owns those words.
      const outcome = await convertRefusalToAsk(tenantId, err);
      if (outcome) {
        // The WORDS are the gate module's, never a second copy written here —
        // two surfaces with two vocabularies for one server answer is how the
        // doctrine's sentence stops being one sentence.
        const gate: AuthorityGate =
          outcome.outcome === 'filed' ? { kind: 'filed' } : { kind: outcome.outcome, message: outcome.message };
        setPctSay({
          subjectId: subject.id,
          line: `${AUTHORITY_GATE_LEAD} ${authorityGateWords(gate)}`,
          bad: true,
        });
      } else {
        setPctSay({
          subjectId: subject.id,
          line: err instanceof Error ? err.message : 'That percentage stayed as it was.',
          bad: true,
        });
      }
    } finally {
      setPctBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [payments, expenses, subjects, paymentSchema, plan] = await Promise.all([
          listAllRecordsWithLinks(tenantId, AA.PAYMENT, { linkTypes: [LINK.PAYMENT_SUBJECT] }),
          listAllRecordsWithLinks(tenantId, AA.EXPENSE, { linkTypes: [LINK.EXPENSE_SUBJECT] }),
          listAllRecordsByType(tenantId, AA.SUBJECT),
          getRecordTypeSchema(tenantId, AA.PAYMENT).catch(() => null),
          // ⚠ CAUGHT, NOT THROWN. A plan that cannot be read must not empty
          // this screen of a household's own records — it withholds the
          // accounting (planHas(null) is false) and leaves the rows.
          getPlan(tenantId).catch(() => null),
        ]);
        if (cancelled) return;

        // One map for both types — an id is unique across them, and the
        // attribution question is the same question.
        const attributed = new Map<string, string | null>();
        for (const r of payments.records) {
          attributed.set(r.id, otherEnd(payments.linksByRecordId[r.id] ?? [], r.id, LINK.PAYMENT_SUBJECT));
        }
        for (const r of expenses.records) {
          attributed.set(r.id, otherEnd(expenses.linksByRecordId[r.id] ?? [], r.id, LINK.EXPENSE_SUBJECT));
        }

        setState({
          loaded: true,
          payments: activeOnly(payments.records),
          expenses: activeOnly(expenses.records),
          subjects: activeOnly(subjects),
          paymentSchema,
          subjectOf: attributed,
          plan,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState((prev) => ({ ...prev, loaded: true }));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, nonce]);

  if (failure) {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Money</div>
        <div class="registry__say">{failure.headline}</div>
        <div class="registry__say-soft">{failure.detail}</div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  // A4's defect 2 — no absence is claimed before the read lands.
  if (!state.loaded) {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Money</div>
        <div class="registry__say">One moment…</div>
      </div>
    );
  }

  const lifecycle = state.paymentSchema?.lifecycle;
  const split = splitMoney(state.payments, lifecycle);
  // ⚑ THE SEVERITY COLOUR COMES WITH THE WORD "LATE", AND ONLY WITH IT.
  // `split.aged` is everything due-or-overdue plus everything with no date at
  // all, so the headline figure it feeds is not by itself an alarm — a row
  // that fell due this morning is in it. What IS an alarm is the part that is
  // past its date, and that is what this counts and what wears the ramp. The
  // engagement's own aside computes the same figure the same way; the rule
  // itself lives in farSide.css beside .fs__money-v--owed.
  const lateTotal = state.payments.reduce((n, p) => {
    if (isSettled(p, lifecycle)) return n;
    const age = ageInDays(p.fields.expected_on);
    return age !== null && age > 0 ? n + outstandingOn(p) : n;
  }, 0);
  const subjectById = new Map(state.subjects.map((s) => [s.id, s]));
  // P6 — THE ONE QUESTION, ASKED ONCE. The literal key is here rather than
  // behind a helper so a reader (and the FeatureGates guard) can see WHICH
  // feature this screen gates on; a screen gated on the wrong one would be
  // indistinguishable from a screen gated correctly.
  const accounting = planHas(state.plan, 'accounting.contracted_income');

  return (
    <div class="registry">
      <div class="registry__eyebrow">Money</div>

      {/* ---------- 0. THE LINE A HOUSEHOLD BELOW PRO READS ----------
          Named and short. It says what is not here and, in the same breath,
          that nothing of theirs is missing — because the first thing anyone
          thinks when a screen is thinner than they remember is "have I lost
          it". No button and no pitch: the tab is not offered at all below
          Pro, so anyone reading this arrived by an old link, and the plans
          page carries the sell. */}
      {!accounting ? (
        <div class="fs__lapse">
          The chase and the set-aside figures come with Pro. Every payment and expense below is yours and stays
          exactly as you recorded it.
        </div>
      ) : null}

      {/* ---------- 1. WHAT IS OWED, AND HOW LATE ---------- */}
      {accounting ? (
        <section>
          <h2 class="registry__sec">The chase</h2>
          {split.aged.count === 0 && split.notYetDue.rows.length === 0 ? (
            <div class="registry__empty">Nothing outstanding.</div>
          ) : (
            <>
              <div class="fs__money-pair">
                <div>
                  <div class="fs__money-k">Outstanding and due</div>
                  <div class={`fs__money-v${split.aged.total > 0 ? ' fs__money-v--owed' : ''}`}>
                    {fmtMoney(split.aged.total)}
                  </div>
                  {lateTotal > 0 ? (
                    <div class="fs__late" data-testid="chase-late">
                      {lateTotal >= split.aged.total
                        ? 'all of it late'
                        : `${fmtMoney(lateTotal)} late`}
                    </div>
                  ) : null}
                </div>
                <div>
                  <div class="fs__money-k">Received</div>
                  {/* P6 — EVERY ROW'S running total, not just the settled ones.
                      A part-payment that is still being chased is money already
                      in the household's hands, and omitting it would have this
                      screen understating what a family actually has. */}
                  <div class="fs__money-v">{fmtMoney(split.receivedTotal)}</div>
                </div>
              </div>

              {/* QUIET, DELIBERATELY. This is real money the household is
                  waiting on and it is NOT a problem — which is the whole
                  reason it was lifted out of the bands. */}
              {split.notYetDue.rows.length > 0 ? (
                <div class="fs__notdue">
                  {fmtMoney(split.notYetDue.total)} not yet due
                  {split.notYetDue.soonest ? `, from ${shortDay(split.notYetDue.soonest)}` : ''}
                </div>
              ) : null}

              {split.aged.bands
                .filter((b) => b.count > 0)
                .map((b) => (
                  <div key={b.key} class="fs__band">
                    <span class="fs__band-dot" style={{ background: b.color }} />
                    <span class="fs__band-label">{b.label}</span>
                    <span class="fs__band-total">{fmtMoney(b.total)}</span>
                  </div>
                ))}
              {/* The honest bucket. NOT on the severity ramp — "nobody recorded
                  a due date" is not a mild aging problem, it is a different
                  kind of fact. */}
              {split.aged.unknown.count > 0 ? (
                <div class="fs__band fs__band--unknown">
                  <span class="fs__band-dot" />
                  <span class="fs__band-label">No due date recorded</span>
                  <span class="fs__band-total">{fmtMoney(split.aged.unknown.total)}</span>
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {/* ---------- 2. WAS HER SHARE SET ASIDE ---------- */}
      {accounting ? (
        <section>
          <h2 class="registry__sec">Should have been set aside</h2>
          {state.subjects.length === 0 ? (
            <div class="registry__empty">No performers recorded yet.</div>
          ) : (
            state.subjects.map((s) => {
              // ⚑ P6 — EVERY payment of hers, not only the settled ones.
              // `setAsideFor` already sums only rows that carry a
              // `received_amount`, so a filter on top of it was doing one of two
              // things: nothing, or — once a payment could be partly received
              // and still outstanding — DROPPING money that has arrived. You set
              // aside a share of what came, and it came whether or not the rest
              // of the invoice has.
              const theirs = state.payments.filter((p) => state.subjectOf.get(p.id) === s.id);
              const out = setAsideFor(theirs, s);
              const editing = pctEdit?.subjectId === s.id;
              const say = pctSay?.subjectId === s.id ? pctSay : null;
              return (
                <div key={s.id} class="fs__setaside">
                  <div class="fs__band">
                    <span class="fs__band-label">{s.display_name}</span>
                    <span class="fs__band-total">
                      {/* NULL IS NOT ZERO. */}
                      {out.shouldHaveBeenSetAside === null ? (
                        <span class="fs__absent">no percentage recorded</span>
                      ) : (
                        `${fmtMoney(out.shouldHaveBeenSetAside)} of ${fmtMoney(out.received)}`
                      )}
                    </span>
                    {/* ⚑ THE DOOR THE FIGURE NEVER HAD (2026-09-16, the Day 3–4
                        walk, item 3). `set_aside_pct` is a real, declared field
                        that this very line reads, and no surface in the product
                        could write it — so the line above could only ever say
                        "no percentage recorded", on every household, forever.
                        It is edited HERE, where it is read, rather than on a
                        performer-settings panel: no such panel exists, and
                        inventing one is a new surface archetype rather than the
                        one field this needs. */}
                    {editing ? null : (
                      <button
                        type="button"
                        class="fs__setaside-open"
                        onClick={() =>
                          setPctEdit({
                            subjectId: s.id,
                            draft: out.pct === null ? '' : String(out.pct),
                          })
                        }
                      >
                        {out.pct === null ? 'Set a percentage' : `${out.pct}% — change`}
                      </button>
                    )}
                  </div>
                  {editing ? (
                    <div class="fs__setaside-edit">
                      <label class="fs__setaside-field">
                        <span>Percentage of what arrives</span>
                        <input
                          class="fs__setaside-input"
                          type="text"
                          inputMode="decimal"
                          autoFocus
                          value={pctEdit.draft}
                          disabled={pctBusy}
                          onInput={(e) =>
                            setPctEdit({ subjectId: s.id, draft: (e.target as HTMLInputElement).value })
                          }
                        />
                      </label>
                      <button
                        type="button"
                        class="fs__setaside-save"
                        disabled={pctBusy}
                        onClick={() => void savePct(s, pctEdit.draft)}
                      >
                        {pctBusy ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        class="fs__setaside-cancel"
                        disabled={pctBusy}
                        onClick={() => {
                          setPctEdit(null);
                          setPctSay(null);
                        }}
                      >
                        Cancel
                      </button>
                      {/* Emptying the box CLEARS it — said here, because
                          "leave it blank" is the one thing a box cannot show. */}
                      <span class="fs__setaside-note">Empty clears it.</span>
                    </div>
                  ) : null}
                  {say ? (
                    <div class={say.bad ? 'fs__setaside-say fs__setaside-say--bad' : 'fs__setaside-say'} role="status">
                      {say.line}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </section>
      ) : null}

      {/* ---------- the rows themselves. ON EVERY TIER. ---------- */}
      <section>
        <h2 class="registry__sec">{sectionOf(AA.PAYMENT).label}</h2>
        {state.payments.length === 0 ? (
          <div class="registry__empty">{sectionOf(AA.PAYMENT).empty}</div>
        ) : (
          <div class="sp__list">
            {state.payments.map((p) => {
              const settled = isSettled(p, lifecycle);
              const age = ageInDays(p.fields.expected_on);
              const late = !settled && age !== null && age > 0;
              // P6 — MONEY THAT CAME IN PIECES SAYS SO, ON ITS OWN ROW.
              // "part" is neither of the two figures beside it: some arrived
              // and some has not, and a row showing ONLY the received amount
              // reads as paid in full while a row showing only the expected
              // one loses the fact that a cheque is already in the bank.
              const received = receivedOn(p);
              const expected = expectedOn(p);
              const part = !settled && received > 0 && expected > 0;
              return (
                <button
                  key={p.id}
                  type="button"
                  class={`sp__row${late ? ' fs__row--late' : ''}${settled ? ' fs__row--done' : ''}`}
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.PAYMENT, id: p.id })}
                >
                  <span class="sp__row-top">
                    <span class="sp__row-name">
                      {typeof p.fields.kind === 'string' ? humanizeValue(p.fields.kind) : 'Payment'}
                    </span>
                    <span class="sp__row-when">
                      {/* NULL IS NOT ZERO — `rowAmount`'s own block. */}
                      {part ? (
                        `${fmtMoney(received)} of ${fmtMoney(expected)}`
                      ) : rowAmount(p) === null ? (
                        <span class="fs__absent">{NO_AMOUNT_RECORDED}</span>
                      ) : (
                        fmtMoney(rowAmount(p)!)
                      )}
                    </span>
                  </span>
                  <span class="sp__row-sub">
                    {[
                      p.lifecycle_state ? humanizeValue(p.lifecycle_state) : null,
                      // WHAT IS STILL OWED, in the same breath as how late it
                      // is. The remainder is the number that gets the phone
                      // picked up, and it is the one figure a partial row did
                      // not have anywhere to say before.
                      part ? `${fmtMoney(outstandingOn(p))} still owing` : null,
                      settled ? null : ageLabel(age),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {/* ⚑ WHOSE MONEY THIS IS, WHEN NOBODY SAID (2026-09-16, the
                      Day 3–4 walk, item 2). A contract that names no performer
                      stages its payments unattached — correctly, because
                      putting one child's money on another's page is worse — and
                      the review card says "say whose this is after approving".
                      Then nothing anywhere named WHICH rows that sentence was
                      about. They are still in the chase (splitMoney reads every
                      row), but the set-aside section above silently skips them,
                      so the one figure this screen owes a child is quietly
                      missing with nothing red. The row says so, and the row is
                      already the door: tapping it opens the payment, where the
                      performer picker is. */}
                  {state.subjectOf.get(p.id) ? null : (
                    // ⚠ A STATE AND ITS CONSEQUENCE, NEVER AN INSTRUCTION
                    // (canon §5: the screen teaches by its shape — and this
                    // row is already the door into the payment, where the
                    // performer picker is). What it says is the true cost:
                    // the set-aside section above reads `subjectOf` and skips
                    // a row nobody is attached to, so the one figure this
                    // screen owes a child goes quietly missing.
                    <span class="fs__unattached" data-testid="payment-unattached">
                      Nobody is attached, so nothing is set aside for this one
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 class="registry__sec">{sectionOf(AA.EXPENSE).label}</h2>
        {state.expenses.length === 0 ? (
          <div class="registry__empty">{sectionOf(AA.EXPENSE).empty}</div>
        ) : (
          <div class="sp__list">
            {state.expenses.map((e) => {
              const who = state.subjectOf.get(e.id) ?? null;
              return (
                <button
                  key={e.id}
                  type="button"
                  class="sp__row"
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.EXPENSE, id: e.id })}
                >
                  <span class="sp__row-top">
                    <span class="sp__row-name">
                      {typeof e.fields.tax_category === 'string' && e.fields.tax_category.trim()
                        ? e.fields.tax_category
                        : 'Expense'}
                    </span>
                    <span class="sp__row-when">
                      {fmtMoney(typeof e.fields.amount === 'number' ? e.fields.amount : 0)}
                    </span>
                  </span>
                  <span class="sp__row-sub">
                    {/* AN ABSENT SUBJECT MEANS "THE HOUSEHOLD'S" — never
                        "unknown". The pack's header is explicit that the
                        absence carries meaning here (a household corporation
                        files for several children), and rendering it as
                        missing data would invert what it says. */}
                    {who && subjectById.has(who) ? subjectById.get(who)!.display_name : "the household's"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

/** A due date, short and LOCAL. The engagement surface formats its copy of
 * this line and this one did not — so the household screen showed a raw
 * `2026-09-18` next to otherwise finished typography. Caught by reading the
 * walk's own screen dump, which is the third time a defect has been visible
 * in output nobody was looking at. */
function shortDay(v: string): string {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
